import React, { useState } from "react";
import { testimonials } from "../mock/data";
import { Star, Quote, ExternalLink, ShieldCheck } from "lucide-react";

const GOOGLE_URL = "https://share.google/fMLSAtIBRGsHXfiEq";

function GoogleG({ size = 14 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width={size} height={size} aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

function Card({ t }) {
  const [expanded, setExpanded] = useState(false);
  const short = t.text.length > 200 && !expanded ? t.text.slice(0, 200) + "…" : t.text;
  return (
    <div className="bg-white rounded-3xl p-7 border border-[#0e2b2b]/10 card-lift">
      <div className="flex items-center justify-between">
        <Quote className="w-7 h-7 text-[#14a68e]/70" />
        <a
          href={GOOGLE_URL}
          target="_blank"
          rel="noreferrer"
          title="Verified Google review"
          className="inline-flex items-center gap-1.5 bg-[#eaf6f2] text-[#0e6b5a] text-[10px] tracking-[0.14em] uppercase font-semibold rounded-full px-2.5 py-1 hover:bg-[#d3ecdf] transition"
        >
          <GoogleG size={11} /> Verified
        </a>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#14a68e] to-[#0f8f7a] text-white flex items-center justify-center font-serif-display text-lg">{t.initial}</div>
        <div>
          <div className="font-serif-display text-[18px] text-[#0e2b2b] leading-tight">{t.name}</div>
          <div className="flex items-center gap-2 mt-0.5">
            <div className="flex gap-0.5">
              {Array(5).fill(0).map((_, i) => (
                <Star key={i} size={13} className="fill-[#14a68e] text-[#14a68e]" />
              ))}
            </div>
            {t.when && <span className="text-[11px] text-[#0e2b2b]/50">· {t.when}</span>}
          </div>
        </div>
      </div>
      <p className="mt-5 text-[14px] text-[#0e2b2b]/75 leading-relaxed">{short}</p>
      {t.text.length > 200 && (
        <button onClick={() => setExpanded(!expanded)} className="mt-3 text-[13px] text-[#14a68e] font-semibold">
          {expanded ? "Read less ↑" : "Read more ↓"}
        </button>
      )}
    </div>
  );
}

export default function Testimonials() {
  return (
    <section id="reviews" className="py-24 md:py-32 bg-[#f6f7f4] relative">
      <div className="max-w-[1400px] mx-auto px-6 md:px-8">
        <div className="relative rounded-[36px] bg-white/70 border border-[#0e2b2b]/10 py-10 px-6 text-center max-w-3xl mx-auto">
          <div className="absolute inset-0 rounded-[36px] soft-glow pointer-events-none" />
          <div className="section-eyebrow relative">Patient Stories</div>
          <h2 className="relative mt-3 font-serif-display text-[40px] md:text-[58px] leading-[1.05] text-[#0e2b2b]">
            What our <span className="hero-italic">patients</span> say
          </h2>

          <div className="relative mt-5 flex items-center justify-center gap-3 text-[13px] text-[#0e2b2b]/75">
            <span className="inline-flex items-center gap-2 bg-white rounded-full px-3 py-1.5 border border-[#0e2b2b]/10 shadow-sm">
              <GoogleG size={14} />
              <span className="font-semibold">5.0</span>
              <div className="flex gap-0.5">
                {Array(5).fill(0).map((_, i) => (
                  <Star key={i} size={12} className="fill-[#14a68e] text-[#14a68e]" />
                ))}
              </div>
              <span className="text-[#0e2b2b]/60">· 150+ Google reviews</span>
            </span>
            <a
              href={GOOGLE_URL}
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-[#14a68e] font-semibold hover:text-[#0f8f7a] transition"
            >
              See all reviews <ExternalLink size={13} />
            </a>
          </div>

          <p className="relative mt-3 text-[13.5px] md:text-[14px] text-[#0e2b2b]/65">
            Real, verified reviews from patients on Google — Dr. Khurana&rsquo;s Dental &amp; Aesthetic Clinic, Chhatameel Lucknow.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, i) => <Card key={i} t={t} />)}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <a href={GOOGLE_URL} target="_blank" rel="noreferrer" className="btn-primary">
            <GoogleG size={14} /> Read all on Google
          </a>
          <a href="#contact" className="btn-ghost">
            <ShieldCheck size={16} /> Book your visit
          </a>
        </div>
      </div>
    </section>
  );
}
